import { useCallback, useMemo } from 'react';
import { Dropdown, Form, Icon, Input, Label, SemanticCOLORS } from 'semantic-ui-react';

// Styled components
import OButton from '../../../styled/button';

// Constants
import Modalities from './options';

// Locales
import I18n from '../../../../i18n';

// Styles
import './index.scss'

type ModalityKey = keyof typeof Modalities;

const MODALITY_KEYS = Object.keys(Modalities) as ModalityKey[];

interface InputValue {
    key: string,
    modality: ModalityKey,
    price: number
}

interface FormSelectParamsInterface {
    fieldClasses?: string,
    label?: string,
    colorLabel?: SemanticCOLORS,
    meta: {
        touched?: boolean,
        invalid?: boolean,
        error?: string
    },
    input: {
        value: InputValue[],
        onChange?: any
    },
    placeholder?: string,
    disabled?: boolean,
    directoryFee: number
};

const generateUniqueKey = () => Date.now().toString(36) + Math.random().toString(36).substring(2);

const CustomPracticeModalities = (params: FormSelectParamsInterface) => {
    const pricingSlots = useMemo(()=> params.input.value || [], [params.input.value]);

    const updateForm = useCallback((newSlots) => {
        params.input.onChange(newSlots);
    }, [params.input]);

    const selectedModalityKeys = useMemo(() => new Set(pricingSlots.map(slot => slot.modality)), [pricingSlots]);

    const availableModalityKeys = useMemo(() => {
        return MODALITY_KEYS.filter(key => !selectedModalityKeys.has(key));
    }, [selectedModalityKeys]);

    const handleAddSlot = useCallback(() => {
        if (availableModalityKeys.length === 0) return;

        const newSlot = {
            key: generateUniqueKey(),
            modality: availableModalityKeys[0],
            price: null,
        };

        updateForm([...pricingSlots, newSlot]);
    }, [pricingSlots, updateForm, availableModalityKeys]);

    const handleRemoveSlot = useCallback((keyToRemove) => {
        const newSlots = pricingSlots.filter(slot => slot.key !== keyToRemove);
        updateForm(newSlots);
    }, [pricingSlots, updateForm]);

    const getFeeValue = useCallback((numValue: number) => {
        const feePercent = params.directoryFee / 100;
        const feeValue = numValue - (numValue * feePercent);

        return `${I18n.t('setup.modalities.fee', { feeValue: `${params.directoryFee }%`})}${feeValue}`
    }, [params.directoryFee]);

    const handleChangeSlot = useCallback((keyToUpdate, field, newValue) => {
        const newSlots = pricingSlots.map(slot => {
            if (slot.key === keyToUpdate) {
                let updatedValue = newValue;
                
                if (field === 'price') {
                    const numValue = parseFloat(newValue);
                    updatedValue = isNaN(numValue) || numValue < 0 ? 0 : numValue;

                    const feeInput = document.getElementById(`fee-input-${keyToUpdate}`) as HTMLInputElement;

                    if (feeInput) {
                        const feeValue = getFeeValue(numValue);

                        feeInput.value = feeValue;
                    }
                }

                return { ...slot, [field]: updatedValue };
            }

            return slot;
        });

        updateForm(newSlots);
    }, [getFeeValue, pricingSlots, updateForm]);

    return (
        <Form.Field className={ params.fieldClasses } error={ params.meta.touched && params.meta.invalid }>
            <div className='modalitiesContainer'>
                {pricingSlots.length === 0 ? (
                    <p>
                        {I18n.t('setup.modalities.noModalities')}
                    </p>
                ) : (
                    // Lista de Precios por Modalidad
                    <div>
                        {pricingSlots.map((slot) => {

                            const feeValue = getFeeValue(slot.price);

                            return <div key={slot.key} className='priceSlot'>
                                {/* Selector de Modalidad */}
                                <Dropdown
                                    className='fluid priceSlotInput'
                                    value={slot.modality}
                                    selection
                                    onChange={(e, { value }) => handleChangeSlot(slot.key, 'modality', value)}
                                    options={MODALITY_KEYS
                                    .filter(key => !selectedModalityKeys.has(key) || key === slot.modality)
                                    .map(key => (
                                        {
                                            key,
                                            text: Modalities[key],
                                            value: key
                                        }
                                    ))}
                                />

                                {/* Selector de Precio */}
                                <Input
                                    className='fluid priceSlotInput'
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={slot.price === null ? '' : slot.price} 
                                    onChange={(e) => handleChangeSlot(slot.key, 'price', e.target.value)}
                                />

                                <Input
                                    id={`fee-input-${slot.key}`}
                                    className='fluid priceSlotInput'
                                    type="text"
                                    readOnly
                                    defaultValue={ feeValue }
                                />

                                {/* Botón de Eliminar */}
                                <OButton
                                    type="button"
                                    onClick={() => handleRemoveSlot(slot.key)}
                                    aria-label={`Eliminar precio para ${Modalities[slot.modality] || 'modalidad'}`}
                                >
                                    {I18n.t('setup.modalities.delete')}
                                </OButton>
                            </div>
                        } )}
                    </div>
                )}

                {availableModalityKeys.length > 0 ? (
                    <OButton
                        type="button"
                        onClick={handleAddSlot}
                    >
                    <Icon name="add" size="small" />
                    <span>{I18n.t('setup.modalities.add')}</span>
                    </OButton>
                ) : (
                    <div>
                        {I18n.t('setup.modalities.allAssigned')}
                    </div>
                )}
            </div>

            { params.meta.touched && params.meta.invalid && <Label basic color={ params.colorLabel || 'red' } pointing>{ params.meta.error }</Label> }
        </Form.Field>
    );

};

export default CustomPracticeModalities;